interface TweenInstance {
  tween: Phaser.Tweens.Tween | null;
  inUse: boolean;
  type: TweenType;
  cleanup?: () => void;
}

export const TweenType = {
  MOVE: "move",
  SCALE: "scale",
  FADE: "fade",
  ROTATE: "rotate",
  DAMAGE_NUMBER: "damage_number",
  PICKUP_BOUNCE: "pickup_bounce",
  UI_ANIMATION: "ui_animation",
} as const;

export type TweenType = (typeof TweenType)[keyof typeof TweenType];

interface TweenConfig {
  targets: any;
  duration: number;
  ease?: string;
  delay?: number;
  repeat?: number;
  yoyo?: boolean;
  onComplete?: () => void;
  onUpdate?: () => void;
  onStart?: () => void;
  // Type-specific properties
  x?: number;
  y?: number;
  scaleX?: number;
  scaleY?: number;
  scale?: number;
  alpha?: number;
  rotation?: number;
  [key: string]: any;
}

/**
 * Tween Pool for efficient tween management
 * Reuses tween instances to prevent memory allocation/deallocation
 */
export default class TweenPool {
  private scene: Phaser.Scene;
  private tweenInstances: Map<TweenType, TweenInstance[]> = new Map();
  private maxInstancesPerType: number = 50; // Increased from 15 to handle more concurrent damage numbers
  private totalMaxInstances: number = 300; // Increased from 100 to match

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.initializePools();
  }

  private initializePools(): void {
    Object.values(TweenType).forEach((type) => {
      this.tweenInstances.set(type, []);
      console.log("[Logs for Dima] Initialized pool for type:", type);
    });

    console.log("[Logs for Dima] TweenPool initialized", this.tweenInstances);
  }

  /**
   * Create a move tween (position change)
   */
  createMoveTween(config: TweenConfig): Phaser.Tweens.Tween | null {
    const moveConfig: TweenConfig = { ...config };

    // Only include position properties if they're defined
    if (config.x !== undefined) {
      moveConfig.x = config.x;
    }
    if (config.y !== undefined) {
      moveConfig.y = config.y;
    }

    return this.createTween(TweenType.MOVE, moveConfig);
  }

  /**
   * Create a scale tween
   */
  createScaleTween(config: TweenConfig): Phaser.Tweens.Tween | null {
    const scaleConfig: TweenConfig = { ...config };

    // Only include scale properties if they're defined
    if (config.scale !== undefined) {
      scaleConfig.scale = config.scale;
    }
    if (config.scaleX !== undefined) {
      scaleConfig.scaleX = config.scaleX;
    }
    if (config.scaleY !== undefined) {
      scaleConfig.scaleY = config.scaleY;
    }

    return this.createTween(TweenType.SCALE, scaleConfig);
  }

  /**
   * Create a fade tween (alpha change)
   */
  createFadeTween(config: TweenConfig): Phaser.Tweens.Tween | null {
    return this.createTween(TweenType.FADE, {
      ...config,
      alpha: config.alpha,
    });
  }

  /**
   * Create a rotation tween
   */
  createRotateTween(config: TweenConfig): Phaser.Tweens.Tween | null {
    return this.createTween(TweenType.ROTATE, {
      ...config,
      rotation: config.rotation,
    });
  }

  /**
   * Create a damage number tween (specialized for damage text)
   */
  createDamageNumberTween(config: TweenConfig): Phaser.Tweens.Tween | null {
    const damageConfig: TweenConfig = { ...config };

    // Only include properties if they're defined
    if (config.y !== undefined) {
      damageConfig.y = config.y;
    }
    if (config.scale !== undefined) {
      damageConfig.scale = config.scale;
    }
    if (config.alpha !== undefined) {
      damageConfig.alpha = config.alpha;
    }

    return this.createTween(TweenType.DAMAGE_NUMBER, damageConfig);
  }

  /**
   * Create a pickup bounce tween
   */
  createPickupBounceTween(config: TweenConfig): Phaser.Tweens.Tween | null {
    const bounceConfig: TweenConfig = { ...config };

    // Only include properties if they're defined
    if (config.y !== undefined) {
      bounceConfig.y = config.y;
    }
    if (config.scaleX !== undefined) {
      bounceConfig.scaleX = config.scaleX;
    } else {
      bounceConfig.scaleX = 1.1;
    }
    if (config.scaleY !== undefined) {
      bounceConfig.scaleY = config.scaleY;
    } else {
      bounceConfig.scaleY = 1.1;
    }

    return this.createTween(TweenType.PICKUP_BOUNCE, bounceConfig);
  }

  /**
   * Create a UI animation tween
   */
  createUIAnimationTween(config: TweenConfig): Phaser.Tweens.Tween | null {
    return this.createTween(TweenType.UI_ANIMATION, config);
  }

  private createTween(
    type: TweenType,
    config: TweenConfig
  ): Phaser.Tweens.Tween | null {
    const instance = this.getTweenInstance(type);
    if (!instance) return null;

    return this.configureTween(instance, config);
  }

  private getTweenInstance(type: TweenType): TweenInstance | null {
    const instances = this.tweenInstances.get(type);
    console.log(
      "[Logs for Dima] Getting tween instance from the list:",
      this.tweenInstances
    );

    if (!instances) {
      return null;
    }

    // Monitor pool usage and warn if approaching limits
    const activeCount = instances.filter((inst) => inst.inUse).length;
    const totalCount = instances.length;
    if (totalCount > this.maxInstancesPerType * 0.8) {
      console.warn(
        `[TweenPool] ${type} pool at ${activeCount}/${totalCount} (${(
          (activeCount / totalCount) *
          100
        ).toFixed(1)}% active), approaching limit of ${
          this.maxInstancesPerType
        }`
      );
    }

    // Find available instance
    let instance = instances?.find((inst) => !inst.inUse);

    if (!instance) {
      // Check if we can create new instance
      if (instances.length >= this.maxInstancesPerType) {
        // Reuse oldest instance
        const oldestInstance = instances[0];
        this.stopTweenInstance(oldestInstance);
        instance = oldestInstance;
      } else {
        // Check total limit
        const totalInstances = Array.from(this.tweenInstances.values()).reduce(
          (total, arr) => total + arr.length,
          0
        );

        if (totalInstances >= this.totalMaxInstances) {
          // Find and reuse oldest instance across all types
          const oldestInstance = this.findOldestInstance();
          if (oldestInstance) {
            this.stopTweenInstance(oldestInstance);
            instance = oldestInstance;
            instance.type = type;
          } else {
            return null;
          }
        } else {
          // Create new instance
          instance = this.createTweenInstance(type);
          instances.push(instance);
        }
      }
    }

    return instance;
  }

  private createTweenInstance(type: TweenType): TweenInstance {
    return {
      tween: null,
      inUse: false,
      type,
    };
  }

  private configureTween(
    instance: TweenInstance,
    config: TweenConfig
  ): Phaser.Tweens.Tween | null {
    // Stop existing tween if any
    if (instance.tween) {
      instance.tween.stop();
      instance.tween.destroy();
    }

    // Validate targets before creating tween
    if (!this.isValidTweenTarget(config.targets)) {
      instance.inUse = false;
      return null;
    }

    // Create clean tween config and add auto-cleanup
    const tweenConfig = this.createCleanTweenConfig(config);
    tweenConfig.onComplete = () => {
      // Clear timeout since tween completed normally
      const timeoutId = (instance as any).timeoutId;
      if (timeoutId) {
        clearTimeout(timeoutId);
        (instance as any).timeoutId = null;
      }

      // Execute the actual callback FIRST to ensure proper cleanup
      if (config.onComplete) {
        try {
          config.onComplete();
        } catch (error) {
          console.error("[TweenPool] Error in onComplete callback:", error);
        }
      }
      // Only THEN mark the instance as available for reuse
      instance.inUse = false;
    };

    try {
      instance.tween = this.scene.tweens.add(tweenConfig);
      instance.inUse = true;

      // Set up cleanup function
      instance.cleanup = () => {
        if (instance.tween) {
          instance.tween.stop();
        }
        // Clear timeout to prevent memory leaks
        const timeoutId = (instance as any).timeoutId;
        if (timeoutId) {
          clearTimeout(timeoutId);
          (instance as any).timeoutId = null;
        }
        instance.inUse = false;
      };

      // Add safety timeout to prevent stuck tweens (10 seconds max)
      const timeoutDuration = Math.max(tweenConfig.duration * 2, 10000);
      const timeoutId = setTimeout(() => {
        if (instance.inUse && instance.tween) {
          console.warn(
            `[TweenPool] Tween timeout reached, forcing cleanup for ${instance.type} tween`
          );
          instance.cleanup?.();
          // Execute callback if it exists to prevent stuck objects
          if (config.onComplete) {
            try {
              config.onComplete();
            } catch (error) {
              console.error("[TweenPool] Error in timeout callback:", error);
            }
          }
        }
      }, timeoutDuration);

      // Store timeout ID for cleanup
      (instance as any).timeoutId = timeoutId;

      return instance.tween;
    } catch (error) {
      instance.inUse = false;
      return null;
    }
  }

  private isValidTweenTarget(target: any): boolean {
    // Basic null/undefined check
    if (!target || typeof target !== "object") {
      return false;
    }

    // Check for essential properties that Phaser tweens expect
    if (!target.hasOwnProperty || typeof target.hasOwnProperty !== "function") {
      return false;
    }

    // Check if target is a valid Phaser game object
    if (target.scene && !target.scene.sys) {
      return false;
    }

    // Check if target is destroyed or inactive
    if (
      target.scene &&
      target.scene.sys &&
      target.scene.sys.game &&
      !target.scene.sys.game.loop
    ) {
      return false;
    }

    // Check if it's a Phaser GameObject and if it's destroyed
    if (target.destroy && target.scene && target.active === false) {
      return false;
    }

    return true;
  }

  private createCleanTweenConfig(config: TweenConfig): any {
    const cleanConfig: any = {};

    // Copy all defined properties from config
    for (const key in config) {
      if (config[key] !== undefined) {
        cleanConfig[key] = config[key];
      }
    }

    return cleanConfig;
  }

  private stopTweenInstance(instance: TweenInstance): void {
    if (instance.cleanup) {
      instance.cleanup();
    }
    if (instance.tween) {
      instance.tween.stop();
      instance.tween.destroy();
      instance.tween = null;
    }
    // Clear timeout to prevent memory leaks
    const timeoutId = (instance as any).timeoutId;
    if (timeoutId) {
      clearTimeout(timeoutId);
      (instance as any).timeoutId = null;
    }
    instance.inUse = false;
  }

  private findOldestInstance(): TweenInstance | null {
    for (const instances of this.tweenInstances.values()) {
      for (const instance of instances) {
        if (instance.inUse) {
          return instance;
        }
      }
    }
    return null;
  }

  /**
   * Stop all tweens of a specific type
   */
  stopAllTweens(type?: TweenType): void {
    if (type) {
      const instances = this.tweenInstances.get(type);
      if (instances) {
        instances.forEach((instance) => this.stopTweenInstance(instance));
      }
    } else {
      // Stop all tweens
      for (const instances of this.tweenInstances.values()) {
        instances.forEach((instance) => this.stopTweenInstance(instance));
      }
    }
  }

  /**
   * Kill all tweens immediately
   */
  killAllTweens(): void {
    for (const instances of this.tweenInstances.values()) {
      instances.forEach((instance) => {
        if (instance.tween) {
          instance.tween.stop();
          instance.tween.destroy();
          instance.tween = null;
        }
        instance.inUse = false;
      });
    }
  }

  /**
   * Cleanup and dispose of all tween instances
   */
  destroy(): void {
    this.killAllTweens();
    this.tweenInstances.clear();
  }

  /**
   * Get pool statistics for monitoring
   */
  getStats(): {
    totalInstances: number;
    activeInstances: number;
    instancesByType: Record<string, number>;
  } {
    let totalInstances = 0;
    let activeInstances = 0;
    const instancesByType: Record<string, number> = {};

    for (const [type, instances] of this.tweenInstances) {
      instancesByType[type] = instances.length;
      totalInstances += instances.length;
      activeInstances += instances.filter((instance) => instance.inUse).length;
    }

    return {
      totalInstances,
      activeInstances,
      instancesByType,
    };
  }
}
